import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';

import HashtagTab from '@components/dataDisplay/hashtag/HashtagTab';
import StoreItem from './StoreItem';
import StaggerMotionWrapper from '@components/wrapper/StaggerMotionWrapper';
import { CollectionPath } from '@typings/collections.enum';
import { scrollbarStyle } from '@styles/variants/scrollbar';
import WalletPrice from '../component/WalletPrice';
import { Ihashtag } from '@typings/common/hashtag.interface';
import { useRecoilValue } from 'recoil';
import { mainPhotocardDataState } from '@atom/setting/photocardAutoSetState';
import {
  canViewStoreCategory,
  canViewStoreItem,
  getHashtagMap,
} from '@utils/store/storeAccess';

const STORE_DOC_LIMIT = 1000;

const Container = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 480px) {
    height: auto;
    overflow: visible;
  }
`;

const ScrollableWrapper = styled.div`
  flex: 1;
  position: relative;
  overflow: hidden;

  @media (max-width: 480px) {
    flex: none;
    overflow: visible;
    height: auto;
  }
`;

const ScrollableContent = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  padding-top: 4px;
  ${scrollbarStyle};

  @media (max-width: 480px) {
    height: auto;
    overflow: visible;
  }
`;

type Props = {
  selectedHashtag?: string;
  showHashtag?: boolean;
  showWallet?: boolean;
};
function StoreItemList({ selectedHashtag = 'all', showHashtag = true, showWallet = true }: Props) {
  const collectionPath = CollectionPath.STORE;
  const [list, setList] = useState<any[] | any>();
  const [hashtagList, setHashtagList] = useState<Ihashtag[]>([]);
  const representativeCharacter = useRecoilValue(mainPhotocardDataState);
  const representativeCampId = representativeCharacter?.camp || '';
  const hashtagMap = useMemo(() => getHashtagMap(hashtagList), [hashtagList]);
  const handleHashtagListChange = useCallback((nextList: Ihashtag[]) => {
    setHashtagList(nextList);
  }, []);
  const accessibleList = useMemo(
    () =>
      (list || []).filter((item) =>
        canViewStoreItem(item, representativeCampId, hashtagMap),
      ),
    [list, representativeCampId, hashtagMap],
  );

  // HashtagTab에 key를 추가하여 selectedHashtag 변경 시 리렌더링 강제
  return (
    <Container>
      <HashtagTab
        key={`hashtab-${selectedHashtag}`}
        docLimit={STORE_DOC_LIMIT}
        collectionPath={collectionPath}
        setList={setList}
        hiddenTopPagination
        noShowCount={true}
        orderDir={'asc'}
        orderBy={'order'}
        hiddenBottomPagination={true}
        noShowHashtag={!showHashtag}
        initialSelectedHashtag={selectedHashtag}
        hashtagFilter={(tag) =>
          canViewStoreCategory(tag, representativeCampId)
        }
        onHashtagListChange={handleHashtagListChange}
        list={
          <>
            {showWallet && <WalletPrice showMyWalet={true} />}
            <ScrollableWrapper>
              <ScrollableContent>
                <StaggerMotionWrapper line={4} mobileLine={2} gap={'8px'}>
                  {list &&
                    accessibleList.map((item) => (
                      <StoreItem
                        key={item.id}
                        data={item}
                        collectionPath={collectionPath}
                      />
                    ))}
                </StaggerMotionWrapper>
              </ScrollableContent>
            </ScrollableWrapper>
          </>
        }
      />
    </Container>
  );
}

export default StoreItemList;
